import React, { useEffect, useState } from "react";
import { observer } from "mobx-react";
import Header from "../../components/Header/Header";
import DataTable from "../../components/table/Table";
import rootStores from "../../stores";
import {
  getAllAreaCodes,
  getAllPriceBandsCodes,
  getAllClubsCodes,
  getAllEventsByClub,
  getAllStadiumsCodes,
  getTicketsCompareData,
} from "../../fetchers/RFIDTickets";
import { getColumns } from "./consts";
import { useTranslation } from "react-i18next";
import { toJS } from "mobx";

const RFIDTicketsStore = rootStores.RFIDTicketsStore;

/* ---------- shared helpers ---------- */

const fetchEventsByClub = (clubPrefix: string) => {
  if (!clubPrefix || clubPrefix === "ADMIN") {
    RFIDTicketsStore.setEvents([]);
    return;
  }

  getAllEventsByClub(clubPrefix)
    .then(events => RFIDTicketsStore.setEvents(events))
    .catch(console.error);
};

const fetchFiltersData = () => {
  getAllAreaCodes().then(areas => {
    RFIDTicketsStore.setAreas(areas);
    RFIDTicketsStore.setAllAreasCodes(areas);
  });

  getAllStadiumsCodes().then(stadiums => {
    RFIDTicketsStore.setStadiums(stadiums);
    RFIDTicketsStore.setAllStadiumsCodes(stadiums);
  });

  getAllPriceBandsCodes().then(priceBands => {
    RFIDTicketsStore.setPriceBands(priceBands);
  });

  getAllClubsCodes().then(clubs => {
    RFIDTicketsStore.setClubs(clubs);
    RFIDTicketsStore.setAllClubsCodes(clubs);

    const selectedClub = RFIDTicketsStore.club ?? clubs[0];

    RFIDTicketsStore.setClub(selectedClub);
    RFIDTicketsStore.setFilterField("clubPrefix", selectedClub?.clubPrefix);

    fetchEventsByClub(selectedClub?.clubPrefix);
  });
};

/* ---------- component ---------- */

const TicketsCompareComponent: React.FC = () => {
  const { t, i18n } = useTranslation();
  const language = i18n.language as "he" | "en";
  const [rows, setRows] = useState<any[]>([]);
  const [pageSize, setPageSize] = useState<number>(100);
  const [isLoading, setIsLoading] = useState(false);
  const [evaXMLCount, setEvaXMLCount] = useState<number | null>(null);
  const [fortressCount, setFortressCount] = useState<number | null>(null);

  const filter = toJS(RFIDTicketsStore.filter);

  /* ---------- lifecycle ---------- */

  useEffect(() => {
    fetchFiltersData();
    return () => RFIDTicketsStore.clearFilter();
  }, []);

  useEffect(() => {
    fetchEventsByClub(RFIDTicketsStore.club?.clubPrefix);
  }, [RFIDTicketsStore.club?.clubPrefix]);

  /* ---------- handlers ---------- */

  const handleFilter = async () => {
    if (!filter.event) {
      alert(t("ticketsCompare.selectEventError"));
      return;
    }

    setIsLoading(true);

    const res = await getTicketsCompareData(
      RFIDTicketsStore.club,
      filter.event
    );

    const mappedRows = res.differentObjects.map(
      (item: any, index: number) => ({ ...item, id: index })
    );

    setRows(mappedRows);
    setEvaXMLCount(res.evaXMLTicketsLength);
    setFortressCount(res.fortressTicketsLength);

    setPageSize(mappedRows.length || 1);

    setIsLoading(false);
  };


  const handleSync = async () => {
    try {
      await RFIDTicketsStore.startPolling();
    } catch (err) {
      console.error("Sync failed", err);
    }
  };

  /* ---------- render ---------- */

  return (
    <div style={{ width: "100%", display: "flex", flexDirection: "row-reverse" }}>
      <div
        style={{
          margin: "30px 50px 0 50px",
          display: "flex",
          flexDirection: "column",
          flexGrow: 1,
        }}
      >
        <Header
          handleFilter={handleFilter}
          isRightDialogOpen={false}
          filterFields={["event"]}
          handleSync={handleSync}
          isSyncing={true}
        />

        <div style={{ display: "flex", justifyContent: "flex-end", margin: "2vh" }}>
          {evaXMLCount !== null && (
            <div style={{ margin: "0 4vh" }}>
              {t("ticketsCompare.evaXmlCount")} : {evaXMLCount}
            </div>
          )}
          {fortressCount !== null && (
            <div style={{ margin: "0 4vh" }}>
              {t("ticketsCompare.fortressCount")} : {fortressCount}
            </div>
          )}
        </div>

        <DataTable
          rows={rows}
          columns={getColumns(language)}
          isLoading={isLoading}
          withCheckbox={false}
          withAllCheckbox={false}
          pageSize={pageSize}
          setPageSize={setPageSize}
          totalRecordsInDB={rows.length}
        />
      </div>
    </div>
  );
};

export default observer(TicketsCompareComponent);
